import React, { useMemo } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";

import {
  BuildServerlessBookCoverImageUrl,
  formatImageUrlByDevice,
  getPageNumberAsInt,
  _isFirstPage,
  _isLastPage,
  getBasePageLink,
  getLastPageNumber,
  getPaginationText,
} from "helpers/Helper";
import Image from "../../Image/Image";
import Pagination from "@components/Pagination/Pagination";
import NewsletterV2 from "@components/NewsletterV2/NewsletterV2";
import { useAppContext } from "@components/Provider";

import "./InterestPageBooks.css";

const InterestPageBooks = ({
  books,
  pageNumber,
  slug,
  newsletterProps,
  isMobile,
  subscriberCookie,
}) => {
  const { deviceType } = useAppContext();
  const pathname = usePathname();

  const pageSize = 50;
  const currentPageNumber = useMemo(() => getPageNumberAsInt(pageNumber), [pageNumber]);
  const totalBooks = books?.length;
  const isFirstPage = useMemo(() => _isFirstPage(currentPageNumber), [currentPageNumber]);
  const isLastPage = useMemo(
    () => _isLastPage(currentPageNumber, totalBooks, pageSize),
    [currentPageNumber, totalBooks, pageSize]
  );
  const slugName = useMemo(() => (slug ? decodeURIComponent(slug) : ""), [slug]);

  const startIndex = (currentPageNumber - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedBooks = useMemo(() => {
    const booksForPage = books?.slice(startIndex, endIndex);
    if (booksForPage?.length > 8 && !subscriberCookie) {
      booksForPage.splice(8, 0, { type: "newsletter-v2" });
    }
    return booksForPage;
  }, [books, startIndex, endIndex]);

  const renderBooks = useMemo(() => {
    return paginatedBooks?.map((book, index) => {
      if (book && book?.type === "newsletter-v2") {
        return (
          <div key={index} className="newsletter-container">
            <NewsletterV2 {...newsletterProps} />
          </div>
        );
      }

      const { title, authors, post, image, urlType } = book;
      const imageUrl =
        urlType === "Internal Link"
          ? BuildServerlessBookCoverImageUrl(
              { width: "170", height: "0" },
              typeof image === "string" ? image : image?.props?.image?.Name
            )
          : formatImageUrlByDevice(
              typeof image === "string" ? image : image?.props?.image?.Name,
              deviceType
            );
      const authorNames =
        authors && typeof authors === "string"
          ? authors
          : authors.map((author) => author.display_name).join(", ");

      const amazonUrl = book?.retailerLinks?.find((el) => el?.name === "Amazon")?.url;

      const renderBuyButton = () => {
        return (
          <>
            {amazonUrl ? (
              <Link className="button" href={amazonUrl} target="_blank" rel="noopener">
                BUY
              </Link>
            ) : null}
          </>
        );
      };

      const renderImage = () => {
        return (
          <>
            {amazonUrl ? (
              <Link href={amazonUrl} target="_blank" rel="noopener">
                <Image
                  ImageUrl={imageUrl}
                  Title={title}
                  Alt_Text={title}
                  width="100"
                  height="150"
                />
              </Link>
            ) : null}
          </>
        );
      };

      return (
        <div key={index} className="books-container">
          <div>
            {renderImage()}
            {isMobile ? <div style={{ margin: "0.5rem auto" }}>{renderBuyButton(book)}</div> : null}
          </div>
          <div className="book-details">
            <div className="title" dangerouslySetInnerHTML={{ __html: title }} />
            <div className="author">by {authorNames || ""}</div>
            <div className="book-actions">
              {!isMobile && renderBuyButton(book)}
              <div className="more-recommendations">
                <div>SEE MORE RECOMMENDATIONS</div>
                <Link href={post?.slug[0]} className="post-link" target="_blank" rel="noopener">
                  <div dangerouslySetInnerHTML={{ __html: post?.title }} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [paginatedBooks, deviceType]);

  return (
    <div className="interest-page-books">
      {renderBooks}
      <Pagination
        isFirstPage={isFirstPage}
        isLastPage={isLastPage}
        basePageLink={getBasePageLink("", slugName)}
        currentPageNumber={currentPageNumber}
        lastPageNumber={getLastPageNumber(totalBooks, pageSize)}
        getLinkHref={pathname}
        paginationText={getPaginationText(totalBooks, currentPageNumber, pageSize, "results")}
        pageSize={pageSize}
      />
    </div>
  );
};

InterestPageBooks.displayName = "InterestPageBooks";

export default InterestPageBooks;
